import React, { useState, useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Play,
  Pause,
  Volume2,
  Upload,
  Music,
  MousePointer,
  Award,
  VolumeX,
  ArrowRight,
  Clock,
  Wand2,
  Sparkles,
  BadgeAlert,
  Check,
  Radio,
  Trophy,
  Disc,
  GamepadIcon,
  RefreshCw,
} from "lucide-react";

// Base URL for sound assets - change this to match your storage URL
const SOUND_BASE_URL = "https://teacherwilliam.sgp1.digitaloceanspaces.com/sounds";

// Define the sound categories with their associated sound files based on the actual directory structure
const SOUND_CATEGORIES = [
  {
    id: "gameplay",
    name: "Gameplay",
    description: "Sounds played during actual gameplay",
    sounds: [
      { id: "card-deal", name: "Card Deal", description: "Played when cards are dealt", url: `${SOUND_BASE_URL}/gameplay/card-deal.mp3` },
      { id: "card-flip", name: "Card Flip", description: "Played when a card is flipped", url: `${SOUND_BASE_URL}/gameplay/card-flip.mp3` },
      { id: "card-place", name: "Card Place", description: "Played when a card is placed on table", url: `${SOUND_BASE_URL}/gameplay/card-place.mp3` },
      { id: "card-select", name: "Card Select", description: "Played when a card is selected", url: `${SOUND_BASE_URL}/gameplay/card-select.mp3` },
      { id: "round-end", name: "Round End", description: "Played at the end of a round", url: `${SOUND_BASE_URL}/gameplay/round-end.mp3` },
      { id: "round-start", name: "Round Start", description: "Played at the start of a round", url: `${SOUND_BASE_URL}/gameplay/round-start.mp3` },
      { id: "turn-change", name: "Turn Change", description: "Played when the turn changes", url: `${SOUND_BASE_URL}/gameplay/turn-change.mp3` }
    ]
  },
  {
    id: "background",
    name: "Background Music",
    description: "Background music for menus and gameplay",
    sounds: [
      { id: "gameplay-bg-1", name: "Gameplay Music 1", description: "Main background music during gameplay", url: `${SOUND_BASE_URL}/background/gameplay-bg-1.mp3` },
      { id: "gameplay-bg-2", name: "Gameplay Music 2", description: "Alternative background music during gameplay", url: `${SOUND_BASE_URL}/background/gameplay-bg-2.mp3` },
      { id: "menu-bg-1", name: "Menu Music 1", description: "Main background music for menus", url: `${SOUND_BASE_URL}/background/menu-bg-1.mp3` },
      { id: "menu-bg-2", name: "Menu Music 2", description: "Alternative background music for menus", url: `${SOUND_BASE_URL}/background/menu-bg-2.mp3` }
    ]
  },
  {
    id: "ui",
    name: "User Interface",
    description: "Sounds played in menus and user interface",
    sounds: [
      { id: "button-click", name: "Button Click", description: "Played when buttons are clicked", url: `${SOUND_BASE_URL}/ui/button-click.mp3` },
      { id: "error", name: "Error", description: "Played when an error occurs", url: `${SOUND_BASE_URL}/ui/error.mp3` },
      { id: "notification", name: "Notification", description: "Played when receiving a notification", url: `${SOUND_BASE_URL}/ui/notification.mp3` },
      { id: "shuffle", name: "Shuffle", description: "Played when shuffling cards", url: `${SOUND_BASE_URL}/ui/shuffle.mp3` },
      { id: "success", name: "Success", description: "Played when an action succeeds", url: `${SOUND_BASE_URL}/ui/success.mp3` },
      { id: "toggle-switch", name: "Toggle Switch", description: "Played when toggling a switch", url: `${SOUND_BASE_URL}/ui/toggle-switch.mp3` }
    ]
  },
  {
    id: "win",
    name: "Win Sounds",
    description: "Sounds played when winning",
    sounds: [
      { id: "achievement-unlock", name: "Achievement Unlock", description: "Played when unlocking an achievement", url: `${SOUND_BASE_URL}/win/achievement-unlock.mp3` },
      { id: "game-win", name: "Game Win", description: "Played when winning a game", url: `${SOUND_BASE_URL}/win/game-win.mp3` },
      { id: "round-win", name: "Round Win", description: "Played when winning a round", url: `${SOUND_BASE_URL}/win/round-win.mp3` },
      { id: "tournament-win", name: "Tournament Win", description: "Played when winning a tournament", url: `${SOUND_BASE_URL}/win/tournament-win.mp3` }
    ]
  },
  {
    id: "lose",
    name: "Lose Sounds",
    description: "Sounds played when losing",
    sounds: [
      { id: "game-lose", name: "Game Lose", description: "Played when losing a game", url: `${SOUND_BASE_URL}/lose/game-lose.mp3` },
      { id: "round-lose", name: "Round Lose", description: "Played when losing a round", url: `${SOUND_BASE_URL}/lose/round-lose.mp3` },
      { id: "tournament-lose", name: "Tournament Lose", description: "Played when losing a tournament", url: `${SOUND_BASE_URL}/lose/tournament-lose.mp3` }
    ]
  }
];

// Interface to track replaced sounds
interface ReplacedSound {
  id: string;
  category: string;
  timestamp: string;
}

export function DefaultSoundsTab() {
  const { toast } = useToast();
  const [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false);
  const [selectedSound, setSelectedSound] = useState<{id: string, name: string, category: string, url: string} | null>(null);
  const [playingAudio, setPlayingAudio] = useState<HTMLAudioElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isConfirmResetDialogOpen, setIsConfirmResetDialogOpen] = useState(false);
  const [replacedSounds, setReplacedSounds] = useState<ReplacedSound[]>([]);
  const [cacheBuster, setCacheBuster] = useState<Record<string, string>>({});
  const [previewAudio, setPreviewAudio] = useState<HTMLAudioElement | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [processingState, setProcessingState] = useState<'idle' | 'deleting' | 'uploading'>('idle');
  
  // Combined delete+replace mutation
  const replaceWithDeleteMutation = useMutation({
    mutationFn: async ({ 
      soundId, 
      category, 
      file 
    }: { 
      soundId: string, 
      category: string, 
      file: File 
    }) => {
      // Start with deletion
      setProcessingState('deleting');
      console.log(`[SoundDebug] Step 1: Deleting existing sound ${category}/${soundId}`);
      
      try {
        // First delete
        await apiRequest(`/api/system/sounds/${category}/${soundId}`, {
          method: 'DELETE',
        });
        
        console.log(`[SoundDebug] Deletion successful, proceeding to upload`);
        
        // Now upload
        setProcessingState('uploading');
        console.log(`[SoundDebug] Step 2: Uploading replacement file for ${category}/${soundId}`);
        
        // Create form data for the upload
        const formData = new FormData();
        formData.append("file", file);
        formData.append("soundId", soundId);
        formData.append("category", category);
        
        // Upload the new file
        const response = await apiRequest('/api/system/sounds/replace', {
          method: 'POST',
          body: formData,
        });
        
        console.log(`[SoundDebug] Upload successful, operation complete`);
        return response;
        
      } catch (error) {
        console.error('[SoundDebug] Operation failed:', error);
        throw error;
      }
    },
    onSuccess: (data: any, variables) => {
      console.log('[SoundDebug] Sound replaced successfully:', data);
      setProcessingState('idle');
      
      // Update the list of replaced sounds
      setReplacedSounds(prev => [
        ...prev.filter(s => !(s.id === variables.soundId && s.category === variables.category)),
        { 
          id: variables.soundId, 
          category: variables.category,
          timestamp: data.timestamp || new Date().toISOString()
        }
      ]);
      
      // Add a cache buster for this sound
      setCacheBuster(prev => ({
        ...prev,
        [`${variables.category}/${variables.soundId}`]: `?t=${Date.now()}`
      }));
      
      toast({
        title: "Sound replaced",
        description: `The ${selectedSound?.name} sound has been successfully replaced.`
      });
      
      // Close the dialog and reset state
      setIsReplaceDialogOpen(false);
      setSelectedFile(null);
      setSelectedSound(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    onError: (error: any) => {
      console.error('[SoundDebug] Error replacing sound:', error);
      setProcessingState('idle');
      toast({
        title: "Error replacing sound",
        description: error.message || "Failed to replace the sound. Please try again.",
        variant: "destructive"
      });
    }
  });
  
  // Reset sound mutation
  const resetSoundMutation = useMutation({
    mutationFn: async ({ soundId, category }: { soundId: string, category: string }) => {
      console.log(`[SoundDebug] Resetting sound: ${soundId} in category ${category}`);
      
      return apiRequest(`/api/system/sounds/reset/${soundId}`, {
        method: 'POST',
        body: JSON.stringify({ category }),
        headers: {
          'Content-Type': 'application/json'
        }
      });
    },
    onSuccess: (data: any, variables) => {
      console.log('[SoundDebug] Sound reset successfully:', data);
      
      // Remove from the list of replaced sounds
      setReplacedSounds(prev => prev.filter(
        s => !(s.id === variables.soundId && s.category === variables.category)
      ));
      
      // Add a cache buster for this sound
      setCacheBuster(prev => ({
        ...prev,
        [`${variables.category}/${variables.soundId}`]: `?t=${Date.now()}`
      }));
      
      toast({
        title: "Sound reset",
        description: `The ${selectedSound?.name} sound has been reset to its original value.`
      });
      
      // Close dialog and reset state
      setIsConfirmResetDialogOpen(false);
      setSelectedSound(null);
    },
    onError: (error: any) => {
      console.error('[SoundDebug] Error resetting sound:', error);
      toast({
        title: "Error resetting sound",
        description: error.message || "Failed to reset the sound. Please try again.",
        variant: "destructive"
      });
    }
  });
  
  // Function to play/pause a sound
  const togglePlayAudio = (url: string) => {
    // Add cache buster to the URL to prevent caching
    const soundParts = url.split('/');
    const soundId = soundParts[soundParts.length - 1].split('.')[0];
    const category = soundParts[soundParts.length - 2];
    const cacheBusterKey = `${category}/${soundId}`;
    
    const playUrl = `${url}${cacheBuster[cacheBusterKey] || ''}`;
    
    console.log(`[SoundDebug] Attempting to play sound: ${playUrl}`);
    
    if (playingAudio && playingAudio.src === playUrl && !playingAudio.paused) {
      console.log('[SoundDebug] Pausing currently playing audio');
      playingAudio.pause();
      setPlayingAudio(null);
    } else {
      if (playingAudio) {
        console.log('[SoundDebug] Stopping previous audio before playing new sound');
        playingAudio.pause();
      }
      
      console.log('[SoundDebug] Creating new Audio object with URL:', playUrl);
      const audio = new Audio(playUrl);
      
      // Add more detailed event listeners for debugging
      audio.addEventListener('loadstart', () => console.log('[SoundDebug] Audio loadstart event'));
      audio.addEventListener('canplay', () => console.log('[SoundDebug] Audio canplay event - Audio can be played'));
      audio.addEventListener('playing', () => console.log('[SoundDebug] Audio playing event - Playback has begun'));
      audio.addEventListener('waiting', () => console.log('[SoundDebug] Audio waiting event - Playback waiting for data'));
      audio.addEventListener('error', (e) => {
        const error = audio.error;
        console.error('[SoundDebug] Audio error event:', e);
        console.error('[SoundDebug] Audio error code:', error ? error.code : 'unknown');
        console.error('[SoundDebug] Audio error message:', error ? error.message : 'unknown');
        
        // Map error codes to human-readable messages
        let errorMessage = "Unknown error";
        if (error) {
          switch (error.code) {
            case MediaError.MEDIA_ERR_ABORTED:
              errorMessage = "Playback aborted by the user";
              break;
            case MediaError.MEDIA_ERR_NETWORK:
              errorMessage = "Network error - please check your internet connection";
              break;
            case MediaError.MEDIA_ERR_DECODE:
              errorMessage = "Audio decoding error - file might be corrupted";
              break;
            case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
              errorMessage = "Audio format not supported or file not found";
              break;
          }
        }
        
        toast({
          title: "Error playing sound",
          description: errorMessage,
          variant: "destructive"
        });
      });
      
      console.log('[SoundDebug] Calling play() on audio object');
      audio.play()
        .then(() => {
          console.log('[SoundDebug] Audio play() promise resolved successfully');
        })
        .catch(error => {
          console.error('[SoundDebug] Error in play() method:', error);
          console.error('[SoundDebug] Error name:', error.name);
          console.error('[SoundDebug] Error message:', error.message);
          
          toast({
            title: "Error playing sound",
            description: `Failed to play sound: ${error.message}. Check console for details.`,
            variant: "destructive"
          });
        });
        
      audio.onended = () => {
        console.log('[SoundDebug] Audio playback ended');
        setPlayingAudio(null);
      };
      
      audio.onpause = () => {
        console.log('[SoundDebug] Audio paused');
        setPlayingAudio(null);
      };
      
      setPlayingAudio(audio);
    }
  };
  
  // Open the replace sound dialog
  const openReplaceDialog = (soundId: string, name: string, category: string, url: string) => {
    setSelectedSound({ id: soundId, name, category, url });
    setIsReplaceDialogOpen(true);
  };
  
  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      // Stop any existing preview when a new file is selected
      if (previewAudio) {
        previewAudio.pause();
        setIsPreviewPlaying(false);
        setPreviewAudio(null);
      }
    }
  };
  
  // Preview the selected sound file
  const previewSelectedSound = () => {
    if (!selectedFile) return;
    
    // If already playing, pause it
    if (isPreviewPlaying && previewAudio) {
      previewAudio.pause();
      setIsPreviewPlaying(false);
      return;
    }
    
    // If we have a preview audio but it's paused, play it again
    if (previewAudio) {
      previewAudio.play()
        .then(() => {
          setIsPreviewPlaying(true);
        })
        .catch(error => {
          console.error('[SoundDebug] Error playing preview:', error);
          toast({
            title: "Error playing preview",
            description: error.message || "Failed to play the sound preview",
            variant: "destructive"
          });
        });
      return;
    }
    
    // Create a new audio object with the file
    const fileUrl = URL.createObjectURL(selectedFile);
    const audio = new Audio(fileUrl);
    
    audio.addEventListener('error', (e) => {
      console.error('[SoundDebug] Preview error:', e);
      toast({
        title: "Preview error",
        description: "Could not preview this audio file",
        variant: "destructive"
      });
    });
    
    audio.onended = () => {
      setIsPreviewPlaying(false);
    };
    
    audio.onpause = () => {
      setIsPreviewPlaying(false);
    };
    
    // Play the audio
    audio.play()
      .then(() => {
        setPreviewAudio(audio);
        setIsPreviewPlaying(true);
      })
      .catch(error => {
        console.error('[SoundDebug] Error playing preview:', error);
        toast({
          title: "Error playing preview",
          description: error.message || "Failed to play the sound preview",
          variant: "destructive"
        });
      });
  };
  
  // Clean up audio objects when dialog closes
  useEffect(() => {
    if (!isReplaceDialogOpen) {
      if (previewAudio) {
        previewAudio.pause();
        URL.revokeObjectURL(previewAudio.src);
        setPreviewAudio(null);
        setIsPreviewPlaying(false);
      }
    }
  }, [isReplaceDialogOpen]);
  
  // Handle replace sound submit
  const handleReplaceSound = () => {
    if (!selectedFile || !selectedSound) return;
    
    // Check if it's an audio file
    if (!selectedFile.type.startsWith('audio/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an audio file (MP3, WAV, etc.)",
        variant: "destructive"
      });
      return;
    }
    
    console.log(`[SoundDebug] Replacing sound: ${selectedSound.name}`);
    console.log(`[SoundDebug] File type: ${selectedFile.type}, size: ${selectedFile.size} bytes`);
    
    // First delete the existing sound file
    setProcessingState('deleting');
    replaceWithDeleteMutation.mutate({
      soundId: selectedSound.id,
      category: selectedSound.category,
      file: selectedFile
    });
  };
  
  // Check if a sound has been replaced
  const isReplaced = (soundId: string, category: string) => {
    return replacedSounds.some(
      s => s.id === soundId && s.category === category
    );
  };
  
  // Get the timestamp when a sound was replaced
  const getReplacedTimestamp = (soundId: string, category: string) => {
    const sound = replacedSounds.find(
      s => s.id === soundId && s.category === category
    );
    return sound ? new Date(sound.timestamp).toLocaleString() : null;
  };
  
  // Function to get the icon for a sound category
  const getCategoryIcon = (categoryId: string) => {
    switch (categoryId) {
      case "gameplay":
        return <GamepadIcon className="h-5 w-5 text-blue-500" />;
      case "ui":
        return <MousePointer className="h-5 w-5 text-green-500" />;
      case "win":
        return <Trophy className="h-5 w-5 text-yellow-500" />;
      case "lose":
        return <VolumeX className="h-5 w-5 text-red-500" />;
      case "background":
        return <Radio className="h-5 w-5 text-purple-500" />;
      default:
        return <Volume2 className="h-5 w-5" />;
    }
  };
  
  // Function to get the icon for a specific sound
  const getSoundIcon = (soundId: string) => {
    switch (true) {
      case soundId.includes('win'):
        return <Trophy className="h-5 w-5 text-yellow-500" />;
      case soundId.includes('lose'):
        return <VolumeX className="h-5 w-5 text-red-500" />;
      case soundId.includes('card'):
        return <ArrowRight className="h-5 w-5 text-blue-500" />;
      case soundId.includes('notification'):
        return <BadgeAlert className="h-5 w-5 text-blue-500" />;
      case soundId.includes('bg'):
        return <Radio className="h-5 w-5 text-purple-500" />;
      case soundId.includes('achievement'):
        return <Award className="h-5 w-5 text-emerald-500" />;
      case soundId.includes('tournament'):
        return <Trophy className="h-5 w-5 text-amber-500" />;
      default:
        return <Volume2 className="h-5 w-5 text-gray-500" />;
    }
  };
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center">
            <Volume2 className="mr-2 h-6 w-6" />
            Default System Sounds
          </h2>
          <p className="text-muted-foreground">
            Manage and replace the default system sound effects
          </p>
        </div>
      </div>
      
      {/* Sound Categories Tabs */}
      <Tabs defaultValue="gameplay" className="w-full">
        <TabsList className="flex w-full overflow-x-auto space-x-2 pb-2">
          {SOUND_CATEGORIES.map(category => (
            <TabsTrigger key={category.id} value={category.id} className="flex items-center gap-2 flex-shrink-0">
              {getCategoryIcon(category.id)}
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>
        
        {SOUND_CATEGORIES.map(category => (
          <TabsContent key={category.id} value={category.id} className="mt-4 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  {getCategoryIcon(category.id)}
                  {category.name}
                </h3>
                <p className="text-sm text-muted-foreground">{category.description}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {category.sounds.map(sound => {
                const replaced = isReplaced(sound.id, category.id);
                const replacedTimestamp = getReplacedTimestamp(sound.id, category.id);
                
                return (
                  <Card key={sound.id} className="overflow-hidden">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        {getSoundIcon(sound.id)}
                        {sound.name}
                      </CardTitle>
                      <CardDescription className="flex justify-between">
                        <span>{sound.description}</span>
                        <span className="text-xs text-gray-500">{sound.id}.mp3</span>
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => togglePlayAudio(sound.url)}
                            className="h-8 w-8"
                            disabled={playingAudio !== null && playingAudio.src !== sound.url}
                          >
                            {playingAudio && 
                             playingAudio.src.includes(sound.url) && 
                             !playingAudio.paused ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                          <span className={`text-xs ${replaced ? 'text-green-400' : 'text-muted-foreground'}`}>
                            {replaced ? `Custom (${replacedTimestamp})` : 'Default sound'}
                          </span>
                        </div>
                        
                        <div className="flex gap-2">
                          {replaced && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedSound({ id: sound.id, name: sound.name, category: category.id, url: sound.url });
                                setIsConfirmResetDialogOpen(true);
                              }}
                              disabled={resetSoundMutation.isPending}
                            >
                              {resetSoundMutation.isPending && selectedSound?.id === sound.id ? (
                                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              ) : null}
                              Reset
                            </Button>
                          )}
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => openReplaceDialog(sound.id, sound.name, category.id, sound.url)}
                            disabled={replaceWithDeleteMutation.isPending}
                          >
                            {replaceWithDeleteMutation.isPending && selectedSound?.id === sound.id ? (
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            ) : null}
                            Replace
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        ))}
      </Tabs>
      
      {/* Replace Sound Dialog */}
      <Dialog open={isReplaceDialogOpen} onOpenChange={setIsReplaceDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Replace System Sound</DialogTitle>
            <DialogDescription>
              Upload a new sound file to replace the default {selectedSound?.name} sound.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="soundFile">Sound File (MP3, WAV)</Label>
              <Input
                id="soundFile"
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="audio/*"
                required
              />
              <p className="text-xs text-muted-foreground">
                Recommended format: MP3 or WAV, max 500KB
              </p>
            </div>

            {selectedFile && (
              <div className="flex items-center gap-2 mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={previewSelectedSound}
                  className="flex items-center gap-2"
                >
                  {isPreviewPlaying ? (
                    <>
                      <Pause className="h-4 w-4" />
                      Pause Preview
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Preview Selected Sound
                    </>
                  )}
                </Button>
                <span className="text-xs text-muted-foreground">
                  {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
                </span>
              </div>
            )}
            
            {selectedSound && (
              <div className="text-sm">
                <p>File will be uploaded to:</p>
                <code className="bg-muted px-2 py-1 rounded text-xs block mt-1 whitespace-normal break-all">
                  {SOUND_BASE_URL}/{selectedSound.category}/{selectedSound.id}.mp3
                </code>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsReplaceDialogOpen(false);
                setSelectedSound(null);
                setSelectedFile(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = "";
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReplaceSound}
              disabled={!selectedFile || replaceWithDeleteMutation.isPending || processingState !== 'idle'}
            >
              {processingState === 'deleting' ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Deleting old file...
                </>
              ) : processingState === 'uploading' ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload & Replace
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reset Sound Confirmation Dialog */}
      <AlertDialog open={isConfirmResetDialogOpen} onOpenChange={setIsConfirmResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Sound to Default</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reset the {selectedSound?.name} sound to its original default? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedSound(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (selectedSound) {
                  resetSoundMutation.mutate({
                    soundId: selectedSound.id,
                    category: selectedSound.category
                  });
                }
              }}
              disabled={resetSoundMutation.isPending}
            >
              {resetSoundMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Reset Sound
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}